import { type SxProps, type Theme, alpha } from "@mui/material/styles";

import type { AppearanceOptions } from "@dialstack/sdk-js";

import { error, misc, neutral, primary, success } from "app/themes/colors";

// Suno skin for the DialStack softphone, modeled on the retired suno-comms
// dialer's look (plum body, chunky rounded keys, white patient cards).
// Colors flow through the SDK's sanctioned `appearance` API where possible;
// everything the SDK doesn't expose (typography, key geometry, shadows,
// press physics) is restyled via the scoped `.ds-*` classes its stylesheet
// attaches to the widget DOM.

// Plum palette lifted from the old dialer (outgoing-call-widget styles).
export const PLUM_900 = "hsl(271, 20%, 22%)";
export const PLUM_700 = "hsl(272, 17%, 34%)"; // old key color
export const PLUM_700_HOVER = "hsl(271, 18%, 27%)";
export const PLUM_600 = "hsl(272, 18%, 42%)";
export const PLUM_500 = "hsl(272, 18%, 48%)"; // old widget body
export const PLUM_400 = "hsl(273, 21%, 54%)"; // old FAB
export const LILAC = "hsl(272, 45%, 86%)";
const MINT = primary[100]; // brightest brand tint — live accents
export const WHITE_INK = "rgba(255, 255, 255, 0.92)";
export const WHITE_SOFT = "rgba(255, 255, 255, 0.68)";

export const softphoneAppearance: AppearanceOptions = {
  theme: "light",
  variables: {
    colorPrimary: LILAC,
    colorSuccess: success[600],
    colorDanger: error[500],
    colorText: WHITE_INK,
    colorTextSecondary: WHITE_SOFT,
    colorSurfaceSubtle: "rgba(255, 255, 255, 0.10)",
    colorBorder: "rgba(255, 255, 255, 0.18)",
    colorBackground: PLUM_500
  }
};

const WIDGET_RIGHT_PX = 30;
const WIDGET_BOTTOM_PX = 20;
const FAB_MEDIUM_SIZE_PX = 48;

// How far in from the viewport's right edge the collapsed FAB reaches.
export const SOFTPHONE_FAB_CORNER_WIDTH_PX =
  WIDGET_RIGHT_PX + FAB_MEDIUM_SIZE_PX;

// Right padding for a composer whose Send button is pinned to the bottom-right
// of the page (patient Messages, leads inbox, chat): the FAB's footprint plus a
// small gap, applied while `useSoftphoneFabInCorner()` is true (SN-19774).
export const SOFTPHONE_FAB_CLEARANCE_PX = SOFTPHONE_FAB_CORNER_WIDTH_PX + 8;

export const widgetContainerSx: SxProps<Theme> = {
  position: "absolute",
  right: WIDGET_RIGHT_PX,
  bottom: WIDGET_BOTTOM_PX,
  zIndex: (theme) => theme.zIndex.snackbar + 1
};

// Capped to the viewport and laid out as a column. When space runs out the
// call cards (`callCardsSx`) give way first; in a window too short even for the
// SDK's in-call view, that view scrolls rather than losing Hang up off the
// bottom edge (ENG-2012).
export const panelSx: SxProps<Theme> = {
  width: 320,
  maxHeight: "calc(100vh - 24px)",
  display: "flex",
  flexDirection: "column",
  borderRadius: "20px",
  overflow: "hidden",
  backgroundColor: PLUM_500,
  border: "1px solid rgba(255, 255, 255, 0.14)",
  boxShadow:
    "0 24px 48px -12px hsla(271, 30%, 18%, 0.45), 0 6px 16px -8px hsla(271, 30%, 18%, 0.3)",
  transformOrigin: "100% 100%",
  animation: "suno-phone-in 0.16s cubic-bezier(0.2, 0, 0, 1)",
  "@keyframes suno-phone-in": {
    from: { opacity: 0, transform: "scale(0.94) translateY(8px)" },
    to: { opacity: 1, transform: "none" }
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" }
};

// The per-call patient / caller cards between the header and the SDK surface.
// The first part of the panel to give way (and scroll) when space runs out —
// its shrink factor dwarfs the SDK surface's — but never below the top card's
// label and caller, or a second call could be on screen with no card at all.
export const callCardsSx: SxProps<Theme> = {
  flex: "0 1000 auto",
  minHeight: 0,
  overflowY: "auto",
  "&:not(:empty)": {
    minHeight: 76,
    // Room for the last card's drop shadow, which the scroll container clips.
    pb: 0.75
  }
};

// A white inset card on the plum widget body — one per call.
export const callCardSx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  px: 1.5,
  py: 1,
  borderRadius: "14px",
  bgcolor: "rgba(255, 255, 255, 0.96)",
  boxShadow: "0 6px 14px -8px hsla(271, 30%, 15%, 0.5)"
};

// The header band doubles as the drag handle (react-draggable `handle`).
export const headerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  px: 1.75,
  py: 1.25,
  cursor: "move",
  userSelect: "none",
  background: `linear-gradient(180deg, ${PLUM_600} 0%, ${PLUM_900} 100%)`,
  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.14)"
};

export const headerTitleSx: SxProps<Theme> = {
  flexGrow: 1,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.02em",
  color: "#FFFFFF",
  lineHeight: 1
};

export const minimizeButtonSx: SxProps<Theme> = {
  color: "rgba(255, 255, 255, 0.85)",
  "&:hover": {
    color: "#FFFFFF",
    backgroundColor: "rgba(255, 255, 255, 0.12)"
  }
};

// ─── Status dot / presence menu (ENG-1999) ─────────────────────────────────
// The header dot is the user's own status: green while reachable, orange
// during call activity, red on Do Not Disturb, amber while the line itself is
// still (re)connecting. It is a button that opens the presence menu.
// "recording" (ENG-2022) is the on-call orange with a record glyph: an ambient
// scribe is capturing a visit, and the phone declines calls until it ends.

export type SoftphoneStatus =
  | "available"
  | "on-call"
  | "dnd"
  | "recording"
  | "connecting";

// Orange sits between the green and red of the other two states; no theme
// token is bright enough to read as an 8-10px dot on the plum header.
const ORANGE = "#FF9F43";

export const STATUS_DOT_COLORS: Record<SoftphoneStatus, string> = {
  available: MINT,
  "on-call": ORANGE,
  dnd: error[400],
  recording: ORANGE,
  connecting: misc.darkYellow
};

/** The header status button: a 28px target wrapped around the 10px dot. */
export const statusButtonSx: SxProps<Theme> = {
  width: 28,
  height: 28,
  p: 0,
  ml: -0.5,
  flexShrink: 0,
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
  "&:focus-visible": {
    outline: `2px solid ${LILAC}`,
    outlineOffset: 1
  }
};

// Do Not Disturb is the one state that must not lean on hue alone: it gets
// the familiar "minus" glyph so it reads for anyone who cannot tell the red
// from the orange.
export const dndGlyphSx = {
  "&::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    top: "50%",
    width: 5,
    height: 1.5,
    transform: "translate(-50%, -50%)",
    borderRadius: 1,
    backgroundColor: "#FFFFFF"
  }
} as const;

// Recording shares on-call's orange, so it carries its own mark too: the white
// centre of a record button.
export const recordGlyphSx = {
  "&::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    top: "50%",
    width: 5,
    height: 5,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF"
  }
} as const;

export const statusDotSx = (status: SoftphoneStatus): SxProps<Theme> => ({
  position: "relative",
  width: 12,
  height: 12,
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: STATUS_DOT_COLORS[status],
  // A light ring keeps the dot's edge at 3:1+ against the plum header whatever
  // the fill: the red alone lands at ~2.3:1 on PLUM_600 (WCAG 1.4.11).
  border: "1.5px solid rgba(255, 255, 255, 0.85)",
  ...(status === "dnd" && dndGlyphSx),
  ...(status === "recording" && recordGlyphSx),
  ...(status === "available" && {
    animation: "suno-phone-live 2.4s ease-out infinite",
    "@keyframes suno-phone-live": {
      "0%": { boxShadow: "0 0 0 0 rgba(93, 244, 234, 0.45)" },
      "70%": { boxShadow: "0 0 0 7px rgba(93, 244, 234, 0)" },
      "100%": { boxShadow: "0 0 0 0 rgba(93, 244, 234, 0)" }
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" }
  })
});

/** Portals to `document.body` like the State dropdown: must clear the panel. */
export const statusMenuSx: SxProps<Theme> = {
  zIndex: (theme) => theme.zIndex.snackbar + 2
};

export const statusMenuPaperSx: SxProps<Theme> = {
  minWidth: 240,
  maxWidth: 280,
  borderRadius: "12px",
  mt: 0.5
};

/** The menu's first line: the status in words, for whoever cannot read the dot. */
export const statusMenuHeaderSx: SxProps<Theme> = {
  lineHeight: 1.4,
  px: 2,
  pt: 1.25,
  pb: 0.5,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: neutral[800], // the lighter greys fall under 4.5:1 at this size
  backgroundColor: "transparent"
};

/** Why the status is what it is, when the user did not pick it themselves. */
export const statusMenuNoteSx: SxProps<Theme> = {
  lineHeight: 1.4,
  px: 2,
  pb: 0.75,
  fontSize: 11,
  color: neutral[800],
  backgroundColor: "transparent"
};

/** The selected row is tinted with its own status colour, not a generic one. */
export const statusMenuItemSx = (status: SoftphoneStatus): SxProps<Theme> => ({
  alignItems: "flex-start",
  gap: 1.25,
  py: 1,
  whiteSpace: "normal",
  "&.Mui-selected": {
    backgroundColor: alpha(STATUS_DOT_COLORS[status], 0.16)
  },
  "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
    backgroundColor: alpha(STATUS_DOT_COLORS[status], 0.28)
  }
});

/** The swatch in each menu row, sitting on the row's first line of text. */
export const statusMenuDotSx = (status: SoftphoneStatus): SxProps<Theme> => ({
  position: "relative",
  width: 12,
  height: 12,
  mt: "4px",
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: STATUS_DOT_COLORS[status],
  ...(status === "dnd" && dndGlyphSx)
});

// `&&` doubles the wrapper class so these rules outrank the SDK's injected
// `.ds-softphone-<uid> .ds-*` rules (which mount later in <head>). Since
// ENG-2226 the SDK surface renders only while the line is idle — the dial
// pad — so these are dial-screen rules; every call screen is Suno's own.
export const softphoneSurfaceSx: SxProps<Theme> = {
  "--ds-font-family": '"LatoRegular", "Lato", sans-serif',
  flex: "0 1 auto",
  minHeight: 0,
  overflowY: "auto",
  // A short window scrolls the dial pad; overlay scrollbars would hide that
  // there is a Call button further down.
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 3,
    backgroundColor: alpha(neutral[50], 0.45)
  },

  "&& .ds-softphone": {
    padding: "14px 12px 20px",
    "--dsd-surface-active": "rgba(255, 255, 255, 0.18)"
  },

  // Connection / error chips
  "&& .ds-chip": {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em"
  },
  "&& .ds-chip-pending, && .ds-chip-off": {
    background: "rgba(255, 255, 255, 0.14)",
    color: WHITE_INK
  },

  // Number display. The SDK flanks the input with a 40px spacer and a 40px
  // backspace, which at 30px left a 10-digit number wider than its box and cut
  // off the last digits. 24px with slimmer flanks fits even a pasted
  // "+1 (834) 061-7100" in the 320px panel.
  "&& .ds-display": { minHeight: 60, gap: "6px" },
  "&& .ds-display-spacer": { width: 36 },
  "&& .ds-destination": {
    fontSize: 24,
    fontWeight: 600,
    color: "#FFFFFF",
    letterSpacing: 0,
    padding: 0,
    fontVariantNumeric: "tabular-nums",
    caretColor: LILAC,
    "&::placeholder": {
      fontSize: 17,
      fontWeight: 500,
      letterSpacing: 0,
      color: WHITE_SOFT,
      opacity: 1
    }
  },
  // The old dialer's rounded lavender backspace tile.
  "&& .ds-backspace": {
    width: 36,
    height: 36,
    fontSize: 20,
    color: "#FFFFFF",
    background: "rgba(255, 255, 255, 0.16)",
    borderRadius: "12px",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.28)" }
  },

  // Keypad — chunky rounded plum keys with letter subtitles, per the old
  // dialer's DigitButton.
  "&& .ds-keypad": { gap: "12px 16px" },
  "&& .ds-key": {
    maxWidth: 80,
    minHeight: 56,
    borderRadius: "18px",
    background: PLUM_700,
    border: "none",
    boxShadow:
      "0 4px 10px -4px hsla(271, 30%, 15%, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
    transition:
      "background-color 0.15s ease, transform 0.1s cubic-bezier(0.2, 0, 0, 1)",
    "&:hover": { background: PLUM_700_HOVER },
    "&:active": { transform: "scale(0.93)", background: PLUM_900 },
    "&:focus-visible": {
      outline: `2px solid ${LILAC}`,
      outlineOffset: 2
    }
  },
  "&& .ds-key-digit": {
    fontSize: 24,
    fontWeight: 700,
    color: "#FFFFFF",
    lineHeight: 1.15
  },
  "&& .ds-key-letters": {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.16em",
    color: "rgba(255, 255, 255, 0.62)"
  },

  // The Call orb
  "&& .ds-action": { width: 62, height: 62 },
  "&& .ds-call": {
    background: `linear-gradient(140deg, ${success[500]} 0%, ${success[700]} 100%)`,
    boxShadow:
      "0 10px 22px -8px rgba(5, 150, 105, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
    transition:
      "transform 0.12s cubic-bezier(0.2, 0, 0, 1), filter 0.15s ease, box-shadow 0.15s ease"
  },
  "&& .ds-call:not(:disabled):hover": {
    filter: "brightness(1.06)",
    transform: "translateY(-1px)",
    boxShadow:
      "0 14px 28px -8px rgba(5, 150, 105, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.25)"
  },
  "&& .ds-call:disabled": {
    background: "rgba(255, 255, 255, 0.18)",
    boxShadow: "none",
    opacity: 1
  },
  // We render our own one-click picker (e911-location-banner) at panel level, so
  // hide the SDK's collapsed two-click amber bar. Target `.ds-e911-root` only —
  // the attended-transfer UI reuses `.ds-e911-btn` via
  // `.ds-consult-actions .ds-e911-btn`, and a `.ds-e911*` wildcard would take
  // its buttons down with it. Note this fails *open*: the SDK sets no `display`
  // on that node, so a vendor class rename yields two visible banners (obvious
  // in review, still functional) rather than none. The colocated
  // `*.styles.test.ts` asserts the class still exists in the shipped bundle.
  "&& .ds-e911-root": { display: "none" }
};

// ─── The call pop and its folded bar (ENG-2226) ────────────────────────────
// A ring opens the pop unfolded: the panel's plum frame, headed by the bar's
// control row, with the call's white card inside. The chevron folds it down
// to a 56px pill in the phone button's corner. Both share the control pair.

const BAR_HEIGHT_PX = 56;

export const callPopSx: SxProps<Theme> = {
  width: 320,
  maxHeight: "calc(100vh - 24px)",
  display: "flex",
  flexDirection: "column",
  borderRadius: "28px",
  overflow: "hidden",
  pb: 1.25,
  backgroundColor: PLUM_500,
  border: "1px solid rgba(255, 255, 255, 0.14)",
  boxShadow:
    "0 24px 48px -12px hsla(271, 30%, 18%, 0.45), 0 6px 16px -8px hsla(271, 30%, 18%, 0.3)",
  transformOrigin: "100% 100%",
  animation: "suno-phone-in 0.16s cubic-bezier(0.2, 0, 0, 1)",
  "@keyframes suno-phone-in": {
    from: { opacity: 0, transform: "scale(0.94) translateY(8px)" },
    to: { opacity: 1, transform: "none" }
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" }
};

// The pop's header is the bar's control row, so Decline and Answer sit in
// the same place whether the pop is folded or not.
export const callPopHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  minHeight: BAR_HEIGHT_PX,
  pl: 1.75,
  pr: 1,
  py: 0.75,
  cursor: "move",
  userSelect: "none",
  background: `linear-gradient(180deg, ${PLUM_600} 0%, ${PLUM_900} 100%)`,
  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.14)"
};

export const callPopHeaderTextSx: SxProps<Theme> = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.2,
  color: "#FFFFFF"
};

export const callBarSx: SxProps<Theme> = {
  width: 320,
  height: BAR_HEIGHT_PX,
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  pl: 1.25,
  pr: 1,
  borderRadius: `${BAR_HEIGHT_PX / 2}px`,
  background: `linear-gradient(180deg, ${PLUM_600} 0%, hsl(271, 23%, 31%) 100%)`,
  color: "#FFFFFF",
  cursor: "move",
  userSelect: "none",
  boxShadow:
    "0 16px 32px -12px hsla(271, 30%, 18%, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.14)",
  "@keyframes suno-bar-ring": {
    "0%": { boxShadow: `0 0 0 0 ${alpha(primary[100], 0.6)}` },
    "70%": { boxShadow: `0 0 0 8px ${alpha(primary[100], 0)}` },
    "100%": { boxShadow: `0 0 0 0 ${alpha(primary[100], 0)}` }
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiAvatar-root": { animation: "none" }
  }
};

export const callBarAvatarSx: SxProps<Theme> = {
  width: 36,
  height: 36,
  fontSize: 14,
  fontWeight: 600,
  flexShrink: 0,
  bgcolor: LILAC,
  color: PLUM_900
};

export const callBarNameSx: SxProps<Theme> = {
  display: "block",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.25,
  color: "#FFFFFF",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

export const callBarFoldButtonSx: SxProps<Theme> = {
  width: 32,
  height: 32,
  flexShrink: 0,
  color: "rgba(255, 255, 255, 0.85)",
  border: "1px solid rgba(255, 255, 255, 0.35)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" }
};

// The round 40px controls: a neutral glass button, with the answer and the
// end/decline in the phone's own green and red so the pair reads the same as
// the SDK's did.
export const callControlSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  flexShrink: 0,
  color: "#FFFFFF",
  backgroundColor: "rgba(255, 255, 255, 0.14)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.24)" },
  "&[aria-pressed='true']": { backgroundColor: LILAC, color: PLUM_900 },
  "&:focus-visible": {
    outline: "3px solid rgba(205, 191, 224, 0.55)",
    outlineOffset: 2
  }
};

export const callControlDangerSx: SxProps<Theme> = {
  backgroundColor: error[500],
  "&:hover": { backgroundColor: error[600] }
};

export const callControlSuccessSx: SxProps<Theme> = {
  backgroundColor: success[600],
  "&:hover": { backgroundColor: success[700] }
};

// ─── The workbench: Suno's own in-call controls (ENG-2226) ───────────────────
export const workbenchControlsRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-around",
  alignItems: "flex-start",
  flexShrink: 0,
  px: 1,
  pt: 1.25,
  pb: 0.5
};

export const workbenchControlSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  color: "#FFFFFF",
  backgroundColor: "rgba(255, 255, 255, 0.14)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.24)" },
  "&[aria-pressed='true']": {
    backgroundColor: LILAC,
    color: PLUM_900,
    boxShadow: "0 6px 14px -6px hsla(271, 30%, 15%, 0.6)"
  },
  "&:focus-visible": {
    outline: "3px solid rgba(205, 191, 224, 0.55)",
    outlineOffset: 2
  },
  // MUI's disabled grey is meant for a white surface and vanishes on plum.
  "&.Mui-disabled": {
    color: "rgba(255, 255, 255, 0.45)",
    backgroundColor: "rgba(255, 255, 255, 0.06)"
  }
};

export const workbenchControlLabelSx: SxProps<Theme> = {
  mt: 0.5,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1,
  color: WHITE_SOFT
};

export const workbenchControlLabelDisabledSx: SxProps<Theme> = {
  color: "rgba(255, 255, 255, 0.55)"
};

// A keypad, the add-call search, the device list or the consult bar, above
// the control row: a glass inset on the plum body.
export const workbenchOverlaySx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  p: 1.25,
  borderRadius: "12px",
  backgroundColor: "rgba(255, 255, 255, 0.12)",
  flexShrink: 0
};

export const workbenchOverlayLabelSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: WHITE_INK
};

export const dtmfReadoutSx: SxProps<Theme> = {
  textAlign: "center",
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: "0.1em",
  color: "#FFFFFF",
  minHeight: 24,
  fontVariantNumeric: "tabular-nums"
};

export const dtmfKeySx: SxProps<Theme> = {
  height: 40,
  borderRadius: "10px",
  fontSize: 18,
  fontWeight: 700,
  color: "#FFFFFF",
  backgroundColor: "rgba(255, 255, 255, 0.14)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.24)" },
  "&:focus-visible": {
    outline: "3px solid rgba(205, 191, 224, 0.55)",
    outlineOffset: 2
  }
};

export const audioDeviceItemSx: SxProps<Theme> = {
  fontSize: 14,
  color: WHITE_INK,
  borderRadius: "8px",
  minHeight: 36,
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: LILAC,
    color: PLUM_900
  }
};

// The phone note on the workbench: a white field inset on the plum body, with
// the same corners as the card above it.
export const callNoteFieldSx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  flexShrink: 0,
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(255, 255, 255, 0.96)",
    borderRadius: "12px",
    fontSize: 14
  },
  "& .MuiInputBase-input::placeholder": { opacity: 0.7 }
};

export const consultBarSx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  px: 1.25,
  py: 1,
  borderRadius: "12px",
  backgroundColor: "rgba(255, 255, 255, 0.12)",
  flexShrink: 0
};

export const consultBarButtonSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: "none",
  whiteSpace: "nowrap",
  px: 1.25
};

export const fabSx: SxProps<Theme> = {
  background: `linear-gradient(140deg, ${PLUM_400} 0%, ${PLUM_600} 90%)`,
  boxShadow:
    "0 12px 26px -10px hsla(271, 30%, 18%, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.18)",
  transition:
    "transform 0.15s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.15s ease, filter 0.15s ease",
  "&:hover": {
    background: `linear-gradient(140deg, ${PLUM_400} 0%, ${PLUM_600} 90%)`,
    filter: "brightness(1.08)",
    transform: "translateY(-2px)",
    boxShadow:
      "0 16px 32px -10px hsla(271, 30%, 18%, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.18)"
  },
  "&:active": { transform: "scale(0.95)" },
  "&:focus-visible": {
    outline: "3px solid rgba(205, 191, 224, 0.55)",
    outlineOffset: 2
  }
};

// Standby (another tab is the softphone leader): the FAB stays discoverable
// but visibly dormant until the user takes the phone over.
export const standbyFabSx: SxProps<Theme> = {
  filter: "grayscale(0.65)",
  opacity: 0.85,
  "&:hover": { filter: "grayscale(0.35) brightness(1.05)", opacity: 1 }
};

// Stands in for the FAB while there is no phone to open yet (session being
// minted, or re-minted after the phone died). Same height, gradient and
// resting shadow as the medium FAB it swaps with, so the corner reads as one
// element changing shape rather than two things trading places.
export const connectingChipSx: SxProps<Theme> = {
  height: 48,
  px: 1,
  borderRadius: "24px",
  color: WHITE_INK,
  background: `linear-gradient(140deg, ${PLUM_400} 0%, ${PLUM_600} 90%)`,
  boxShadow:
    "0 12px 26px -10px hsla(271, 30%, 18%, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.18)",
  fontWeight: 600,
  "& .MuiChip-icon": { color: WHITE_SOFT, ml: 1 },
  // A spinner that can sit for the whole of a backend outage: freeze it to a
  // static arc for users who asked for less motion, like the panel entrance
  // and the live dot above.
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiCircularProgress-root, & .MuiCircularProgress-circle": {
      animation: "none"
    }
  }
};

export const standbyPopoverSx: SxProps<Theme> = {
  p: 2,
  maxWidth: 300,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.5
};

// Icon + text row at the top of the standby popover.
export const standbyNoticeSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5
};

export const standbyNoticeIconSx: SxProps<Theme> = {
  flexShrink: 0,
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  bgcolor: neutral[100],
  color: PLUM_600
};

// The other tab is on a call: green, like the live-call states elsewhere.
export const standbyNoticeIconOnCallSx: SxProps<Theme> = {
  bgcolor: success[50],
  color: success[700]
};

// ─── E911 location picker ──────────────────────────────────────────────────
// `E911LocationBanner` renders at panel level, outside `softphoneSurfaceSx`, so
// it is styled directly against the plum body rather than through the SDK's CSS
// variables. It must stay visible on the Patients tab, where the SDK surface is
// `display: none` but outbound calls are blocked by the same rule.

/** The white inset card, same idiom as the incoming/outgoing patient cards. */
export const e911BannerSx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  px: 1.5,
  py: 1.25,
  borderRadius: "14px",
  bgcolor: "rgba(255, 255, 255, 0.96)",
  boxShadow: "0 6px 14px -8px hsla(271, 30%, 15%, 0.5)",
  // An amber edge carries "action needed" without tinting the card body, which
  // would cost the address rows their text contrast.
  borderLeft: `3px solid ${misc.darkYellow}`,
  display: "flex",
  flexDirection: "column",
  gap: 0.75
};

export const e911HeadingSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.35,
  color: neutral[800],
  m: 0
};

export const e911HintSx: SxProps<Theme> = {
  fontSize: 11,
  lineHeight: 1.4,
  // `neutral[800]` is the theme's own `text.secondary`. The lighter greys read
  // as "secondary" but measure 2.97:1 (600) and 3.83:1 (700) on this card, and
  // these lines carry real information — which copy is the duplicate, how many
  // were hidden, where 911 would be sent. Size and weight carry the hierarchy.
  color: neutral[800]
};

export const e911ErrorSx: SxProps<Theme> = {
  fontSize: 11,
  lineHeight: 1.4,
  color: error[600]
};

/** One full-width button per address — clicking it *is* the confirmation. */
export const e911AddressButtonSx: SxProps<Theme> = {
  justifyContent: "flex-start",
  textAlign: "left",
  textTransform: "none",
  width: "100%",
  px: 1,
  py: 0.75,
  borderRadius: "10px",
  color: neutral[800],
  backgroundColor: neutral[50],
  // The row's fill is within 1.02:1 of the card, so this border is the only
  // thing that says "control" — at `neutral[200]` it measured 1.25:1, well
  // under the 3:1 of WCAG 1.4.11, and the rows read as disabled input fields.
  border: `1px solid ${PLUM_400}`,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.3,
  "&:hover": { backgroundColor: LILAC, borderColor: PLUM_600 },
  "&.Mui-disabled": { color: neutral[600], opacity: 0.7 },
  "&:focus-visible": { outline: `3px solid ${PLUM_400}`, outlineOffset: 1 }
};

/** The quiet single line shown once a location is bound; sits on plum. */
export const e911BoundRowSx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  display: "flex",
  alignItems: "baseline",
  gap: 0.75,
  minWidth: 0
};

export const e911BoundTextSx: SxProps<Theme> = {
  fontSize: 11,
  // Not WHITE_SOFT (3.33:1 on plum): this line is the only statement of where a
  // 911 call would send paramedics, so it has to be legible, not ambient.
  color: WHITE_INK,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const e911TextButtonBase = {
  textTransform: "none",
  fontSize: 11,
  fontWeight: 600,
  minWidth: 0,
  flexShrink: 0,
  // `&&` because the theme scopes button padding as
  // `.MuiButton-root.MuiButton-sizeMedium` (`app/themes/components/buttons.ts`),
  // which outspecifies a plain `sx` `p` and silently keeps the 46px box — the
  // label then centres ~15px below the sentence it belongs to, reading as a
  // stray control on its own line. Keeps a 24px target (WCAG 2.5.8) with the
  // padding, rather than shrinking to the glyphs.
  "&&": { px: 0.5, py: 0.25 },
  // Not `flex-start`: that pins the button's *box* to the text's top edge, so a
  // vertically centred label lands below the line. Baseline puts the label on
  // the sentence's own baseline, and degrades to start-alignment in the column
  // containers ("Use it"), where it must not stretch to full width.
  alignSelf: "baseline",
  // Underlined at rest, not just on hover. These sit inline with prose
  // ("Saved twice as 500 7th — Remove"), and their colour alone is 2.38:1
  // against the body text next to them, short of the 3:1 that would let colour
  // carry it (WCAG 1.4.1). Hover thickens rather than adds, so the affordance
  // is never colour-only.
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": { background: "none", textDecorationThickness: "2px" }
} as const;

/**
 * "Change" — on the plum body. Lilac is the dialer's interactive colour but tops
 * out at 3.37:1 there, and no tint of it clears 4.5:1 against this plum without
 * going white, so the underline carries the affordance instead.
 */
export const e911TextButtonSx: SxProps<Theme> = {
  ...e911TextButtonBase,
  color: WHITE_INK
};

/** "Show all saved locations" / "Cancel" — inside the white card. */
export const e911CardTextButtonSx: SxProps<Theme> = {
  ...e911TextButtonBase,
  color: PLUM_600
};

/** Denser create-address fields, so six of them fit a 320px panel. */
export const e911FieldSx: SxProps<Theme> = {
  "& .MuiInputBase-input": { fontSize: 12, py: 0.75 },
  "& .MuiInputLabel-root": { fontSize: 12 },
  "& .MuiFormHelperText-root": { fontSize: 10, mx: 0 }
};

/**
 * The State dropdown's menu, which MUI portals to `document.body` at
 * `zIndex.modal` (1300) — below `widgetContainerSx`'s `snackbar + 1`, so the menu
 * opens *behind* the panel. That is not cosmetic: State is required, so a
 * practice with no saved address could never save its first one, and outbound
 * calling stays blocked. `snackbar + 2` is the layer the shared confirm dialog
 * already uses to clear the dialer.
 */
export const e911SelectMenuSx: SxProps<Theme> = {
  zIndex: (theme) => theme.zIndex.snackbar + 2
};

/**
 * The unknown-caller dialogs opened from the panel (Attach to patient, Save to
 * Rolodex, Save as lead). A MUI Dialog sits at `zIndex.modal`, below the
 * panel, so on a laptop-width screen its bottom-right buttons land under the
 * card they were opened from and the clicks never arrive. The same layer as
 * the shared confirm dialog.
 */
export const softphoneDialogSx: SxProps<Theme> = {
  zIndex: (theme) => theme.zIndex.snackbar + 2
};

/**
 * Keeps the 50-state list short inside a 320px panel. Belongs on the menu's
 * Paper, not the menu root — the root is a full-viewport overlay, where a max
 * height constrains nothing.
 */
export const e911SelectMenuPaperSx: SxProps<Theme> = { maxHeight: 320 };

/**
 * Amber dot on the collapsed FAB while a 911 location is still needed, so a
 * minimised dialer does not hide the one thing blocking outbound calls. The
 * `color="error"` red is left to real call activity.
 */
export const e911FabBadgeSx: SxProps<Theme> = {
  "& .MuiBadge-badge": {
    bgcolor: misc.darkYellow,
    // The dot straddles the FAB's plum edge and whatever page is behind it, so
    // it carries its own outline rather than relying on either backdrop.
    border: `1.5px solid ${PLUM_600}`
  }
};

// "Call from" (ENG-1938): a white inset row like the call cards, holding the
// number picker, shown only while the practice has more than one number and
// the line is idle.
export const callerIdRowSx: SxProps<Theme> = {
  mx: 1.25,
  mt: 1.25,
  px: 1.5,
  py: 1,
  borderRadius: "14px",
  bgcolor: "rgba(255, 255, 255, 0.96)",
  boxShadow: "0 6px 14px -8px hsla(271, 30%, 15%, 0.5)"
};

export const callerIdFieldSx: SxProps<Theme> = {
  "& .MuiInputBase-input": { fontSize: 12, py: 0.75 },
  "& .MuiInputLabel-root": { fontSize: 12 }
};

// The device row (ENG-2183) is one line: the label sits left and the choice,
// an underline-free select, right — the same card chrome as "Call from" at
// about half its height, so two rows still leave the keypad above the fold.
export const dialDeviceRowSx: SxProps<Theme> = {
  ...callerIdRowSx,
  py: 0.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  fontSize: 12,
  color: neutral[800]
};

export const dialDeviceFieldSx: SxProps<Theme> = {
  minWidth: 0,
  "& .MuiSelect-select": {
    fontSize: 12,
    fontWeight: 600,
    py: 0.5,
    pr: "28px !important",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }
};

export const callerIdOptionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.25,
  fontSize: 13
};

export const callerIdOptionNumberSx: SxProps<Theme> = {
  fontSize: 11,
  color: neutral[600]
};

// Between the request and the platform ringing this softphone back there is
// nothing on the line yet; say so where the call would otherwise appear.
export const providerDialPendingSx: SxProps<Theme> = {
  ...callerIdRowSx,
  display: "flex",
  alignItems: "center",
  gap: 1,
  fontSize: 12,
  color: neutral[800]
};

// Suno's own Transfer trigger for the two-call state (ENG-2135): the SDK's grid
// swaps its Transfer control for Merge calls once two calls are both connected.
// Outlined on plum, sized to sit under the call cards without crowding the grid.
export const twoCallTransferSx = {
  alignSelf: "center",
  mx: 1.5,
  mb: 1,
  color: WHITE_INK,
  borderColor: LILAC,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { borderColor: WHITE_INK, backgroundColor: PLUM_600 },
  "&[aria-pressed='true']": {
    borderColor: WHITE_INK,
    backgroundColor: PLUM_600
  }
};

// --- Hotdesking (ENG-2293): the desk-phone picker and the walk-up prompt ---

// A visible focus ring, as the E911 rows have: a lilac fill alone is 1.45:1
// against the card and disappears in forced-colours mode.
const deskPhoneFocusRing = {
  "&.Mui-focusVisible": { outline: `3px solid ${PLUM_400}`, outlineOffset: 1 }
};

// The picker is a call card that is the panel's whole body while open, so it
// may scroll on a short viewport; its header stays put at the top.
export const deskPhonePickerSx: SxProps<Theme> = {
  ...callCardSx,
  mb: 1.25,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minHeight: 0,
  overflowY: "auto"
};

// The same picker inside a Settings dialog, which brings its own title and
// white surface: no card chrome, and the dialog's larger type.
export const deskPhonePickerEmbeddedSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25
};

export const deskPhonePickerHeaderSx: SxProps<Theme> = {
  position: "sticky",
  top: -8,
  zIndex: 1,
  mt: -1,
  pt: 1,
  pb: 0.25,
  bgcolor: "rgba(255, 255, 255, 0.96)",
  display: "flex",
  alignItems: "center",
  gap: 0.5
};

// Front-desk users are often 50+: nothing under 12px, the name biggest; the
// greys that fall under 4.5:1 on this card are never used for text.
export const deskPhonePickerTitleSx: SxProps<Theme> = {
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.35,
  color: neutral[800],
  m: 0,
  outline: "none"
};

export const deskPhoneHintSx: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.4,
  color: neutral[800],
  outline: "none"
};

export const deskPhoneBackButtonSx: SxProps<Theme> = {
  color: PLUM_600,
  fontSize: 20,
  p: 0.75,
  ml: -0.75,
  ...deskPhoneFocusRing
};

export const deskPhoneListSx: SxProps<Theme> = {
  p: 0,
  m: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.25
};

// A phone row: name, then whose it is, in words, then the chevron that says
// it does something. A row that is already yours is shown but not clickable,
// and keeps its full contrast.
export const deskPhoneRowSx: SxProps<Theme> = {
  borderRadius: "10px",
  px: 1,
  py: 0.75,
  display: "flex",
  alignItems: "center",
  gap: 1,
  "&.Mui-disabled": { opacity: 1 },
  "&:hover": { bgcolor: LILAC },
  ...deskPhoneFocusRing
};

export const deskPhoneRowTextSx: SxProps<Theme> = { minWidth: 0, flex: 1 };

export const deskPhoneRowChevronSx: SxProps<Theme> = {
  color: PLUM_600,
  fontSize: 20,
  flexShrink: 0
};

export const deskPhoneRowTitleSx: SxProps<Theme> = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.35,
  color: neutral[800]
};

export const deskPhoneRowDetailSx: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.35,
  color: neutral[800]
};

export const deskPhoneStateTitleSx: SxProps<Theme> = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.35,
  color: neutral[800],
  m: 0,
  outline: "none"
};

export const deskPhoneStateBodySx: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.4,
  color: neutral[800]
};

export const deskPhoneStateStrongSx: SxProps<Theme> = {
  ...deskPhoneStateBodySx,
  fontWeight: 700
};

// Inside the Settings dialog the app's dialog type applies.
export const deskPhoneEmbeddedTitleSx: SxProps<Theme> = { fontSize: 16 };
export const deskPhoneEmbeddedBodySx: SxProps<Theme> = { fontSize: 15 };

export const deskPhoneActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  flexWrap: "wrap",
  mt: 0.5
};

// The one committing action is the app's contained button; the quiet one is
// a text link of the same height, so the two labels sit on one line.
export const deskPhonePrimaryButtonSx: SxProps<Theme> = {
  textTransform: "none",
  fontSize: 13,
  minHeight: 32,
  ...deskPhoneFocusRing
};

export const deskPhoneTextButtonSx: SxProps<Theme> = {
  textTransform: "none",
  fontSize: 13,
  fontWeight: 600,
  minHeight: 32,
  px: 1,
  color: PLUM_600,
  textDecoration: "underline",
  "&:hover": { textDecoration: "underline", bgcolor: LILAC },
  ...deskPhoneFocusRing
};

export const deskPhoneSpinnerRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1
};

// The walk-up prompt: a call card with a lilac edge (plum on the plum panel
// is invisible), one line and two buttons.
export const walkUpBannerSx: SxProps<Theme> = {
  ...callCardSx,
  py: 0.75,
  borderLeft: `3px solid ${LILAC}`,
  display: "flex",
  flexDirection: "column",
  gap: 0.25
};
